/**
 * AASB 121 — The Effects of Changes in Foreign Exchange Rates. Rulepack v1.0.0.
 * Validates monetary item translation: AUD = foreign × applied rate; spot vs
 * applied rate at year-end; functional-currency reasonableness.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb121-1.0.0';
const fb = findingBuilder('AASB_121' as any, VERSION);

const HMAP = makeHeaderMap([
  ['exposure_id', 'exposure id', 'transaction id', 'id'],
  ['currency', 'currency', 'ccy'],
  ['foreign_amount', 'foreign amount', 'foreign currency amount', 'fc amount'],
  ['applied_rate', 'applied rate', 'fx rate', 'rate', 'translation rate'],
  ['spot_rate', 'spot rate', 'closing rate', 'reporting date rate'],
  ['aud_amount', 'aud amount', 'aud equivalent', 'reported amount'],
  ['monetary_flag', 'monetary', 'monetary item', 'is monetary'],
  ['as_at_date', 'as at', 'as at date'],
]);

const REQUIRED = ['currency', 'foreign_amount', 'applied_rate', 'aud_amount'];

export function detectAASB121(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB121(headers: string[], data: CellValue[][], asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_121' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('exposure_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB121.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const ccy = String(get('currency') ?? '').toUpperCase().trim();
    const foreignAmt = parseNumber(get('foreign_amount'))!;
    const appliedRate = parseNumber(get('applied_rate'))!;
    const audAmt = parseNumber(get('aud_amount'))!;
    const spotRate = idx.spot_rate !== undefined ? parseNumber(get('spot_rate')) : null;
    const isMonetary = idx.monetary_flag !== undefined ? Boolean(get('monetary_flag')) : null;
    const rowAsAt = idx.as_at_date !== undefined ? (parseDate(get('as_at_date')) ?? asAt) : asAt;

    // R02 currency sanity
    if (ccy === 'AUD') {
      findings.push(fb.build('AASB121.R02', 'info', 'Local currency AUD — translation not required',
        `Exposure ${id}: AUD already; this row should not need foreign-exchange treatment.`,
        { rowId: id }));
    }

    // R03 AUD = foreign × rate
    if (appliedRate !== 0) {
      const expectedAud = foreignAmt * appliedRate;
      const tol = Math.max(50, 0.005 * Math.abs(expectedAud));
      if (Math.abs(audAmt - expectedAud) > tol) {
        findings.push(fb.build('AASB121.R03', 'error', 'AUD amount does not equal foreign × applied rate',
          `Exposure ${id}: foreign ${ccy} ${fmt(foreignAmt)} × rate ${appliedRate} = AUD $${fmt(expectedAud)}, but reported $${fmt(audAmt)}.`,
          { rowId: id, evidence: { foreignAmt, appliedRate, expectedAud, audAmt } }));
      }
    }

    // R04 monetary items at year-end should use closing/spot rate
    if (isMonetary === true && spotRate !== null && spotRate > 0) {
      const drift = Math.abs(appliedRate - spotRate) / spotRate;
      if (drift > 0.005) {
        findings.push(fb.build('AASB121.R04', 'warning', 'Monetary item not translated at closing rate',
          `Exposure ${id}: monetary item, applied rate ${appliedRate}, spot rate ${spotRate} (drift ${(drift * 100).toFixed(2)}%). AASB 121.23 requires monetary items to be translated using the closing rate at the reporting date.`,
          { rowId: id, evidence: { applied: appliedRate, spot: spotRate, drift } }));
      }
    }

    // R05 non-monetary at historic rate — info reminder
    if (isMonetary === false && spotRate !== null && Math.abs(appliedRate - spotRate) / spotRate > 0.01) {
      findings.push(fb.build('AASB121.R05', 'info', 'Non-monetary item — historic rate appropriate',
        `Exposure ${id}: non-monetary item retained at historic rate ${appliedRate} (spot ${spotRate}). AASB 121.23 — non-monetary items at cost remain at historic rate.`,
        { rowId: id }));
    }

    // R06 zero-rate guard
    if (appliedRate <= 0) {
      findings.push(fb.build('AASB121.R06', 'error', 'Applied rate must be positive',
        `Exposure ${id}: applied rate ${appliedRate}.`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_121' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
